import axios, { AxiosError } from "axios";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useEffect, useState, useMemo } from "react";

class CustomerRateLimitError extends AxiosError {
  constructor(
    error: AxiosError,
    readonly retryAt: number
  ) {
    super(
      error.message,
      error.code,
      error.config,
      error.request,
      error.response
    );
  }
}

/** Keep an absolute deadline on the cached error, even if a refetch is blocked. */
export function useCustomerQueryGuard(queryKey: QueryKey) {
  const client = useQueryClient();
  const query = client.getQueryCache().find({ queryKey, exact: true });
  const gate = useMemo<{ error?: CustomerRateLimitError }>(
    () => ({
      error:
        query?.state.error instanceof CustomerRateLimitError
          ? query.state.error
          : undefined,
    }),
    [query]
  );
  if (query?.state.error instanceof CustomerRateLimitError)
    gate.error = query.state.error;
  return async <T>(read: () => Promise<T>): Promise<T> => {
    if (gate.error && Date.now() < gate.error.retryAt) throw gate.error;
    try {
      const result = await read();
      gate.error = undefined;
      return result;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        gate.error = new CustomerRateLimitError(
          error,
          customerRetryAt(error, Date.now())
        );
        throw gate.error;
      }
      throw error;
    }
  };
}

export function customerRetryAt(error: unknown, failedAt: number): number {
  if (error instanceof CustomerRateLimitError) return error.retryAt;
  if (!axios.isAxiosError(error) || error.response?.status !== 429) return 0;
  const value = error.response.headers["retry-after"];
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    const deadline = failedAt + Number(value) * 1000;
    if (Number.isSafeInteger(deadline)) return deadline;
  }
  const isHttpDate =
    typeof value === "string" &&
    /^[A-Za-z]{3}, \d{2} [A-Za-z]{3} \d{4} \d{2}:\d{2}:\d{2} GMT$/.test(value);
  const date = isHttpDate ? Date.parse(value) : NaN;
  return Number.isFinite(date) ? date : failedAt + 60_000;
}
const canRefresh = (query: {
  state: { error: unknown; errorUpdatedAt: number };
}) =>
  Date.now() >= customerRetryAt(query.state.error, query.state.errorUpdatedAt);
export const customerReadPolicy = {
  retry: (count: number, error: unknown) => {
    const status = axios.isAxiosError(error)
      ? error.response?.status
      : undefined;
    return !(status && status >= 400 && status < 500) && count < 2;
  },
  retryDelay: (count: number) => Math.min(1000 * 2 ** count, 30_000),
  refetchOnWindowFocus: canRefresh,
  refetchOnReconnect: canRefresh,
  refetchOnMount: canRefresh,
  retryOnMount: false,
};

/** One wake-up at the deadline; no per-second countdown or mutation retries. */
export function useCustomerReadRecovery(
  error: unknown,
  errorUpdatedAt: number
) {
  const retryAt = customerRetryAt(error, errorUpdatedAt);
  const [, wake] = useState(0);
  useEffect(() => {
    const remaining = retryAt - Date.now();
    if (remaining <= 0) return;
    const timer = setTimeout(() => wake((value) => value + 1), remaining + 1);
    return () => clearTimeout(timer);
  }, [retryAt]);
  return { isCoolingDown: retryAt > Date.now(), retryAt };
}
